/**
 * Erklärbereich für das Partnerprogramm. Wird oben im Partner-Dashboard
 * angezeigt, damit neue Affiliates (oft pädagogische Fachkräfte ohne
 * Affiliate-Vorwissen) sofort verstehen, wie das System funktioniert.
 *
 * Platzhalter für ein Begrüßungsvideo ist bereits vorgesehen –
 * sobald die Video-URL/Vimeo-ID feststeht, kann sie hier eingesetzt werden.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link2, Share2, Euro, PlayCircle, Sparkles, ShieldCheck } from "lucide-react";

interface PartnerHowItWorksProps {
  /** Optionaler Vimeo-/YouTube-Embed-Code oder URL. Wenn leer, wird ein Platzhalter angezeigt. */
  videoEmbedUrl?: string;
}

const steps = [
  {
    icon: Link2,
    title: "1. Link erstellen",
    text: "Gehe auf „Meine Links“, wähle einen Kurs oder eine Seite von MusiKonzept und erzeuge deinen persönlichen Partner-Link. Dein Code ist automatisch enthalten.",
  },
  {
    icon: Share2,
    title: "2. Link teilen",
    text: "Teile den Link überall, wo du Kolleginnen, Eltern oder Fachkräfte erreichst – z.B. in WhatsApp-Gruppen, per E-Mail, auf Instagram, in deinem Blog oder als QR-Code auf einem Flyer.",
  },
  {
    icon: Euro,
    title: "3. Provision erhalten",
    text: "Kauft jemand über deinen Link einen Kurs oder eine Mitgliedschaft, bekommst du automatisch eine Provision gutgeschrieben. Nach Ablauf der 30-Tage-Widerrufsfrist ist sie auszahlbar.",
  },
];

const PartnerHowItWorks = ({ videoEmbedUrl }: PartnerHowItWorksProps) => {
  return (
    <section aria-labelledby="how-it-works-heading" className="mb-8 space-y-6">
      <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background">
        <CardHeader>
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-wide">Willkommen im Partnerprogramm</span>
          </div>
          <CardTitle id="how-it-works-heading" className="text-2xl md:text-3xl">
            So funktioniert's
          </CardTitle>
          <p className="text-muted-foreground">
            Du empfiehlst MusiKonzept weiter – wir bedanken uns mit einer attraktiven Provision.
            Hier siehst du in 3 Schritten, wie du loslegst. Keine Vorkenntnisse nötig.
          </p>
        </CardHeader>

        <CardContent className="grid gap-6 lg:grid-cols-5">
          {/* Video-Platzhalter (linke Spalte) */}
          <div className="lg:col-span-2">
            <div className="relative aspect-video w-full overflow-hidden rounded-lg border bg-muted">
              {videoEmbedUrl ? (
                <iframe
                  src={videoEmbedUrl}
                  title="Begrüßungsvideo – So funktioniert das Partnerprogramm"
                  className="absolute inset-0 h-full w-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                  <PlayCircle className="h-12 w-12" />
                  <p className="text-sm font-medium">Begrüßungsvideo folgt in Kürze</p>
                  <p className="px-4 text-center text-xs">
                    Hier zeigt dir Maja in wenigen Minuten, wie du deine Links einsetzt.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Schritte (rechte Spalte) */}
          <ol className="grid gap-4 lg:col-span-3">
            {steps.map((s) => (
              <li key={s.title} className="flex gap-4 rounded-lg border bg-card/60 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {/* Zusätzliche Hinweise / FAQ-Kurzfassung */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <div className="mb-1 flex items-center gap-2 text-primary">
              <ShieldCheck className="h-4 w-4" />
              <CardTitle className="text-base">Cookie-Laufzeit: 60 Tage</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Klickt jemand auf deinen Link, wirst du auch dann als Empfehlung erkannt,
              wenn der Kauf erst Wochen später erfolgt.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="mb-1 flex items-center gap-2 text-primary">
              <Euro className="h-4 w-4" />
              <CardTitle className="text-base">Auszahlung ab 25 €</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Sobald dein freigegebenes Guthaben 25 € erreicht hat, kannst du es im
              Tab „Auszahlungen“ auf dein Konto überweisen lassen.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="mb-1 flex items-center gap-2 text-primary">
              <Sparkles className="h-4 w-4" />
              <CardTitle className="text-base">Ideen zum Teilen</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Erzähle Kolleg:innen von deinem Lieblingskurs, poste eine kurze
              Erfahrung in deiner Kita-Gruppe oder verlinke uns in deiner
              E-Mail-Signatur. Authentische Empfehlungen wirken am besten.
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default PartnerHowItWorks;
